// useRelease — the release COMMAND CENTER hook set. One composed GET drives the
// whole 8-section shell (`GET /releases/{id}` → ReleaseRead { data: ReleaseCommandCenter, version }); every
// section reads its slice of that single cached query (React Query dedupes, so a deep-link to any section
// fetches once). Section EDITS go through the per-resource endpoints — overview/details/timeline/budget/
// smart-link function against the API in (assets/dsp/ads editors land in). All hooks are bound to
// one releaseId and built on useBandQuery/useBandMutation so keys are ['band', bandId, …] (fix).
//
// Optimistic-concurrency: the release aggregate carries a top-level `version` (its e_ver). Full-set
// writes that bump the root — overview PUT, credits replace, smart-link slug — REQUIRE `If-Match: "<version>"`
// (409 version-conflict, and smart-link additionally 409 slug-taken). Per-row CRUD (milestones, budget lines)
// does NOT require If-Match. Every mutation is server-confirm THEN repaint and surfaces the server error
// verbatim — never a phantom success. Cancel = soft-delete DELETE (the Danger Zone).
import { useQueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandContext } from "../bandContext.js";
import { useBandQuery, useBandMutation } from "./useBandQuery.js";
import { ApiError, errorMessage } from "./apiError.js";
import { pushToast } from "../toasts.js";
import { releaseKey, releasesPrefix, type ReleaseRead } from "./useReleases.js";

type ReleaseCommandCenter = components["schemas"]["ReleaseCommandCenter"];
type UpdateReleaseRequest = components["schemas"]["UpdateReleaseRequest"];
type ReleaseOverview = components["schemas"]["ReleaseOverview"];
type MilestonesRead = components["schemas"]["MilestonesRead"];
type MilestoneUpsertRequest = components["schemas"]["MilestoneUpsertRequest"];
type BudgetRead = components["schemas"]["BudgetRead"];
type BudgetLineUpsertRequest = components["schemas"]["BudgetLineUpsertRequest"];
type CreditsRead = components["schemas"]["CreditsRead"];
type CreditsReplaceRequest = components["schemas"]["CreditsReplaceRequest"];
type AssetsRead = components["schemas"]["AssetsRead"];
type AssetUpsertRequest = components["schemas"]["AssetUpsertRequest"];
type DspLinksRead = components["schemas"]["DspLinksRead"];
type DspLinksReplaceRequest = components["schemas"]["DspLinksReplaceRequest"];
type ReleaseTracksRead = components["schemas"]["ReleaseTracksRead"];
type AddTrackVariantRequest = components["schemas"]["AddTrackVariantRequest"];
type TrackInput = components["schemas"]["TrackInput"];
type SpotifyTrackSyncResult = components["schemas"]["SpotifyTrackSyncResult"];
type DspLinkSyncResult = components["schemas"]["DspLinkSyncResult"];
type SmartLinkRead = components["schemas"]["SmartLinkRead"];
type SmartLinkStatsRead = components["schemas"]["SmartLinkStatsRead"];

export type { SmartLinkStatsRead };

export type {
  ReleaseCommandCenter,
  ReleaseOverview,
  UpdateReleaseRequest,
  MilestoneUpsertRequest,
  BudgetLineUpsertRequest,
  CreditsReplaceRequest,
  AssetUpsertRequest,
  DspLinksReplaceRequest,
  TrackInput,
};

// ---- the composed command-center query -----------------------------------------------------------------

/**
 * `GET /bands/{bandId}/releases/{releaseId}` — the COMPOSED 8-section payload (overview, tracks, assets,
 * to-do, dsp-links, smart-link, ads-shell, credits, milestones, budget) + the aggregate `version`. Keyed
 * ['band', bandId, 'release', releaseId]; a 404 (deleted / not this band) surfaces as the query error → the
 * shell's "not available" state. Every section component calls this same hook (deduped), so the hero and each
 * section share one cache entry and one refetch on the `release-changed` signal.
 */
export function useRelease(releaseId: string | undefined) {
  const { api } = useApp();
  return useBandQuery<ReleaseRead>(
    ["release", releaseId ?? "none"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/releases/{releaseId}", {
        params: { path: { bandId, releaseId: releaseId as string } },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data;
    },
    { enabled: releaseId != null && releaseId !== "", staleTime: 20_000 },
  );
}

/**
 * `GET /bands/{bandId}/releases/{releaseId}/smart-link/stats` — the visit/click stats behind the
 * Smart Link stat tiles + lifetime DSP-clicks bar chart, read from the `analytics.release_smartlink_stats` rollup
 * projection. Keyed ['band', bandId, 'release', releaseId, 'smart-link-stats'] (a prefix of the release key, so a
 * release edit's invalidation refetches it too). Hourly-updated data → a relaxed staleTime. The leak contract is
 * server-side: `visitToClickPct` is `null` when there are no visits (→ "—"), never a fabricated 0.
 */
export function useSmartLinkStats(releaseId: string | undefined) {
  const { api } = useApp();
  return useBandQuery<SmartLinkStatsRead>(
    ["release", releaseId ?? "none", "smart-link-stats"],
    async (bandId) => {
      const { data, error, response } = await api.GET(
        "/api/v1/bands/{bandId}/releases/{releaseId}/smart-link/stats",
        { params: { path: { bandId, releaseId: releaseId as string } } },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { enabled: releaseId != null && releaseId !== "", staleTime: 60_000 },
  );
}

// ---- release-level mutations ---------------------------------------------------------------------------

/** Options shared by the section-mutation hooks. `silent` suppresses the global toast so a MODAL caller that
 *  renders its own inline `role="alert"` is the single error surface (non-modal callers keep the toast). */
export interface MutationOpts {
  silent?: boolean;
}

/** The onError handler for a section mutation; a `silent` caller (modal with inline error) suppresses the toast. */
function toastError(silent: boolean | undefined, fallback: string) {
  return silent ? undefined : (err: unknown) => pushToast(errorMessage(err, fallback));
}

/** Invalidate the composed record + the hub list (title/status/date/slug edits repaint the grid too). */
function useReleaseInvalidator(releaseId: string) {
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: releaseKey(bandId, releaseId) });
    void qc.invalidateQueries({ queryKey: releasesPrefix(bandId) });
  };
}

/**
 * `PUT /bands/{bandId}/releases/{releaseId}` (overview) — edit the release fields (title, type, status,
 * date, UPC, short blurb, TikTok timestamp, budget/funding totals). Sends `If-Match: "<version>"` (409
 * version-conflict on a stale read). Server-confirm THEN repaint — the overview form surfaces the error verbatim.
 */
/**
 * The whole-record body for the release PUT, with one section's edits applied on top.
 *
 * The endpoint replaces the record rather than patching it, and every field on `UpdateReleaseRequest` is
 * OPTIONAL — so a section that forgets a field silently wipes it, with no type error and no server complaint.
 * That is not hypothetical: four sections each hand-assembled this object, so adding `spotifyAlbumId` meant
 * four identical edits, and missing one would have unlinked a release from its tracks when someone saved their
 * budget. The field list lives here once; a section names only what it is actually changing.
 */
export function releaseUpdateBody(
  overview: ReleaseOverview,
  patch: Partial<UpdateReleaseRequest> = {},
): UpdateReleaseRequest {
  return {
    title: overview.title ?? "",
    releaseType: overview.releaseType ?? "single",
    status: overview.status ?? "draft",
    releaseDate: overview.releaseDate,
    upc: overview.upc,
    spotifyAlbumId: overview.spotifyAlbumId,
    shortBlurb: overview.shortBlurb,
    tiktokTimestamp: overview.tiktokTimestamp,
    labelName: overview.labelName,
    // Details "IDs & ownership" metadata (ART-201) — plain, non-money fields the SERVER takes at face value, so
    // like `labelName` they MUST ride every whole-record PUT or an unrelated section save would blank them.
    coArtist: overview.coArtist,
    copyright: overview.copyright,
    publisher: overview.publisher,
    pro: overview.pro,
    isrc: overview.isrc,
    currency: overview.currency,
    coverMediaId: overview.coverMediaId,
    // `totalBudgetCents` / `fundingCents` are deliberately ABSENT from this whole-record body, and their absence
    // is safe rather than the wipe this helper exists to prevent. They ride `money:marketing` and live on the
    // gated `budget` block, not on the overview — a seat without the token is served neither, so there is
    // nothing here to round-trip. The SERVER carries them forward for such a seat and ignores what arrives, so
    // the money-blind majority cannot blank a budget by saving a title. The Budget section, which is the only
    // place they are edited and only reachable WITH the token, passes them through `patch`.
    ...patch,
  };
}

export function useUpdateRelease(releaseId: string) {
  const { api } = useApp();
  const invalidate = useReleaseInvalidator(releaseId);

  return useBandMutation<ReleaseRead, { version: number; body: UpdateReleaseRequest }>(
    async (band, { version, body }) => {
      const { data, error, response } = await api.PUT("/api/v1/bands/{bandId}/releases/{releaseId}", {
        params: { path: { bandId: band, releaseId } },
        headers: { "If-Match": `"${version}"` },
        body,
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data;
    },
    { onSuccess: invalidate, onError: (err) => pushToast(errorMessage(err, "Couldn't save the release")) },
  );
}

/**
 * `DELETE /bands/{bandId}/releases/{releaseId}` (Danger Zone) — cancel = SOFT delete (status='cancelled'
 * + deleted_on; tagged ad campaigns stay, their release tag clears). 204 no content. Invalidates the hub so the
 * cancelled release leaves In-cycle/Released and the grid repaints.
 */
export function useCancelRelease(releaseId: string) {
  const { api } = useApp();
  const invalidate = useReleaseInvalidator(releaseId);

  return useBandMutation<void, void>(
    async (band) => {
      const { error, response } = await api.DELETE("/api/v1/bands/{bandId}/releases/{releaseId}", {
        params: { path: { bandId: band, releaseId } },
      });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    { onSuccess: invalidate, onError: (err) => pushToast(errorMessage(err, "Couldn't cancel the release")) },
  );
}

// ---- Timeline (milestones — per-row CRUD, no If-Match) ---------------------------------------------

/** `POST /releases/{id}/milestones` — add a timeline milestone → MilestonesRead. Repaints the composed record. */
export function useAddMilestone(releaseId: string) {
  const { api } = useApp();
  const invalidate = useReleaseInvalidator(releaseId);
  return useBandMutation<MilestonesRead, MilestoneUpsertRequest>(
    async (band, body) => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/releases/{releaseId}/milestones", {
        params: { path: { bandId: band, releaseId } },
        body,
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { onSuccess: invalidate, onError: (err) => pushToast(errorMessage(err, "Couldn't add the milestone")) },
  );
}

/** `PUT /releases/{id}/milestones/{milestoneId}` — edit a milestone → MilestonesRead. */
export function useUpdateMilestone(releaseId: string) {
  const { api } = useApp();
  const invalidate = useReleaseInvalidator(releaseId);
  return useBandMutation<MilestonesRead, { milestoneId: string; body: MilestoneUpsertRequest }>(
    async (band, { milestoneId, body }) => {
      const { data, error, response } = await api.PUT(
        "/api/v1/bands/{bandId}/releases/{releaseId}/milestones/{milestoneId}",
        { params: { path: { bandId: band, releaseId, milestoneId } }, body },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { onSuccess: invalidate, onError: (err) => pushToast(errorMessage(err, "Couldn't save the milestone")) },
  );
}

/** `DELETE /releases/{id}/milestones/{milestoneId}` — remove a milestone → MilestonesRead. */
export function useDeleteMilestone(releaseId: string) {
  const { api } = useApp();
  const invalidate = useReleaseInvalidator(releaseId);
  return useBandMutation<MilestonesRead, string>(
    async (band, milestoneId) => {
      const { data, error, response } = await api.DELETE(
        "/api/v1/bands/{bandId}/releases/{releaseId}/milestones/{milestoneId}",
        { params: { path: { bandId: band, releaseId, milestoneId } } },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { onSuccess: invalidate, onError: (err) => pushToast(errorMessage(err, "Couldn't remove the milestone")) },
  );
}

// ---- Budget (lines — per-row CRUD, no If-Match; ad_spend_sync line is server-owned) ----------------

/** `POST /releases/{id}/budget-lines` — add a manual budget line → BudgetRead. */
export function useAddBudgetLine(releaseId: string) {
  const { api } = useApp();
  const invalidate = useReleaseInvalidator(releaseId);
  return useBandMutation<BudgetRead, BudgetLineUpsertRequest>(
    async (band, body) => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/releases/{releaseId}/budget-lines", {
        params: { path: { bandId: band, releaseId } },
        body,
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { onSuccess: invalidate, onError: (err) => pushToast(errorMessage(err, "Couldn't add the budget line")) },
  );
}

/** `PUT /releases/{id}/budget-lines/{lineId}` — edit a manual line → BudgetRead (the server rejects ad-sync edits). */
export function useUpdateBudgetLine(releaseId: string) {
  const { api } = useApp();
  const invalidate = useReleaseInvalidator(releaseId);
  return useBandMutation<BudgetRead, { lineId: string; body: BudgetLineUpsertRequest }>(
    async (band, { lineId, body }) => {
      const { data, error, response } = await api.PUT(
        "/api/v1/bands/{bandId}/releases/{releaseId}/budget-lines/{lineId}",
        { params: { path: { bandId: band, releaseId, lineId } }, body },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { onSuccess: invalidate, onError: (err) => pushToast(errorMessage(err, "Couldn't save the budget line")) },
  );
}

/** `DELETE /releases/{id}/budget-lines/{lineId}` — remove a manual line → BudgetRead. */
export function useDeleteBudgetLine(releaseId: string) {
  const { api } = useApp();
  const invalidate = useReleaseInvalidator(releaseId);
  return useBandMutation<BudgetRead, string>(
    async (band, lineId) => {
      const { data, error, response } = await api.DELETE(
        "/api/v1/bands/{bandId}/releases/{releaseId}/budget-lines/{lineId}",
        { params: { path: { bandId: band, releaseId, lineId } } },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { onSuccess: invalidate, onError: (err) => pushToast(errorMessage(err, "Couldn't remove the budget line")) },
  );
}

// ---- Details (credits — full-set replace, REQUIRES If-Match) ---------------------------------------

/**
 * `PUT /releases/{id}/credits` — replace the writers/musicians/production credits set. Sends
 * `If-Match: "<version>"` (the credits replace bumps the release e_ver). Split totals ≠100% WARN, never block
 * (the server returns `splitWarning`), so the client sends whatever the tables hold. 409 on a stale version.
 */
export function useReplaceCredits(releaseId: string) {
  const { api } = useApp();
  const invalidate = useReleaseInvalidator(releaseId);
  return useBandMutation<CreditsRead, { version: number; body: CreditsReplaceRequest }>(
    async (band, { version, body }) => {
      const { data, error, response } = await api.PUT("/api/v1/bands/{bandId}/releases/{releaseId}/credits", {
        params: { path: { bandId: band, releaseId } },
        headers: { "If-Match": `"${version}"` },
        body,
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { onSuccess: invalidate, onError: (err) => pushToast(errorMessage(err, "Couldn't save the credits")) },
  );
}

// ---- Smart link (slug config — REQUIRES If-Match; 409 version-conflict | slug-taken) ---------------

/**
 * `PUT /releases/{id}/smart-link` — set the release's public slug. Sends `If-Match: "<version>"` (the
 * slug rides the release root e_ver → the Antenna picker). A 409 is EITHER `version-conflict` (stale read) OR
 * `slug-taken` (UNIQUE(band_id, slug) collision) — the caller inspects `ApiError.problem.type` to show the
 * right message. Success returns SmartLinkRead; invalidates the record + the hub (the slug shows on the hub
 * Smart Links panel). Slug edits repaint the public URL everywhere it renders.
 */
export function useUpdateSmartLink(releaseId: string, opts?: MutationOpts) {
  const { api } = useApp();
  const invalidate = useReleaseInvalidator(releaseId);
  return useBandMutation<SmartLinkRead, { version: number; slug: string }>(
    async (band, { version, slug }) => {
      const { data, error, response } = await api.PUT("/api/v1/bands/{bandId}/releases/{releaseId}/smart-link", {
        params: { path: { bandId: band, releaseId } },
        headers: { "If-Match": `"${version}"` },
        body: { slug },
      });
      // The PUT documents a 409 (version-conflict | slug-taken); a 409 throws → verbatim toast (no phantom
      // success). On success it returns SmartLinkRead carrying the NEW aggregate version — the smart-link editor
      // chains a following dsp-links write off that version, so editing both in one Save can't self-409.
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { onSuccess: invalidate, onError: toastError(opts?.silent, "Couldn't save the smart link") },
  );
}

// ---- Assets (per-row CRUD, no If-Match; progress is server-derived, never stored) ----------------

/**
 * `POST /releases/{id}/assets` — register an asset tile (cover/lyrics/video/canvas/photo). The body
 * carries the already-uploaded `mediaId` (from the flow, {@link useUploadMedia}), or a `url` (video
 * links), or `textContent` (lyrics). Returns AssetsRead; repaints the composed record so the tile + the derived
 * progress bar update. Server-confirm THEN repaint — a failed write surfaces verbatim (no phantom success).
 */
export function useCreateAsset(releaseId: string, opts?: MutationOpts) {
  const { api } = useApp();
  const invalidate = useReleaseInvalidator(releaseId);
  return useBandMutation<AssetsRead, AssetUpsertRequest>(
    async (band, body) => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/releases/{releaseId}/assets", {
        params: { path: { bandId: band, releaseId } },
        body,
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { onSuccess: invalidate, onError: toastError(opts?.silent, "Couldn't save the asset") },
  );
}

/** `PUT /releases/{id}/assets/{assetId}` — edit an asset tile's media/url/text/role/caption → AssetsRead.*/
export function useUpdateAsset(releaseId: string, opts?: MutationOpts) {
  const { api } = useApp();
  const invalidate = useReleaseInvalidator(releaseId);
  return useBandMutation<AssetsRead, { assetId: string; body: AssetUpsertRequest }>(
    async (band, { assetId, body }) => {
      const { data, error, response } = await api.PUT(
        "/api/v1/bands/{bandId}/releases/{releaseId}/assets/{assetId}",
        { params: { path: { bandId: band, releaseId, assetId } }, body },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { onSuccess: invalidate, onError: toastError(opts?.silent, "Couldn't save the asset") },
  );
}

/** `DELETE /releases/{id}/assets/{assetId}` — remove an asset tile → AssetsRead (progress re-derives).*/
export function useDeleteAsset(releaseId: string, opts?: MutationOpts) {
  const { api } = useApp();
  const invalidate = useReleaseInvalidator(releaseId);
  return useBandMutation<AssetsRead, string>(
    async (band, assetId) => {
      const { data, error, response } = await api.DELETE(
        "/api/v1/bands/{bandId}/releases/{releaseId}/assets/{assetId}",
        { params: { path: { bandId: band, releaseId, assetId } } },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { onSuccess: invalidate, onError: toastError(opts?.silent, "Couldn't remove the asset") },
  );
}

// ---- Songs (the release tracks, full-set replace, REQUIRES If-Match) -----------------------------

/**
 * `PUT /releases/{id}/tracks` (Songs) — replace the track set (TrackId-preserving sync server-side). The
 * Songs list adds a track (title + optional uploaded master `masterMediaId`) by sending the full list. Sends
 * `If-Match: "<version>"` (the tracks replace bumps the release e_ver). 409 on a stale version. Returns
 * ReleaseTracksRead; repaints the composed record (the Songs list + the Details Codes/ISRC table).
 */
export function useReplaceTracks(releaseId: string, opts?: MutationOpts) {
  const { api } = useApp();
  const invalidate = useReleaseInvalidator(releaseId);
  return useBandMutation<ReleaseTracksRead, { version: number; tracks: TrackInput[] }>(
    async (band, { version, tracks }) => {
      const { data, error, response } = await api.PUT("/api/v1/bands/{bandId}/releases/{releaseId}/tracks", {
        params: { path: { bandId: band, releaseId } },
        headers: { "If-Match": `"${version}"` },
        body: { tracks },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { onSuccess: invalidate, onError: toastError(opts?.silent, "Couldn't save the songs") },
  );
}

/**
 * `POST /releases/{id}/tracks/spotify-sync` (ART-219) — the Audio card's "Fetch from Spotify" / "Sync tracks".
 * `removeUnmatched` is the person's answer about songs that match nothing on the album: null on the first press
 * (the server writes nothing and answers `needs-decision` if there are any), true to remove them, false to keep
 * them. Every non-`synced` status is an ANSWER, not an error, so it resolves and the caller words it.
 */
export function useSyncTracksFromSpotify(releaseId: string) {
  const { api } = useApp();
  const invalidate = useReleaseInvalidator(releaseId);
  return useBandMutation<SpotifyTrackSyncResult, { removeUnmatched: boolean | null }>(
    async (band, { removeUnmatched }) => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/releases/{releaseId}/tracks/spotify-sync", {
        params: { path: { bandId: band, releaseId } },
        body: { removeUnmatched },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { onSuccess: invalidate, onError: toastError(false, "Couldn't sync with Spotify") },
  );
}

/** `POST /releases/spotify-track-name` (ART-219) — "Check Spotify ID": what Spotify calls the song a pasted link
 *  names, before it is saved. Stores nothing. A non-track paste (400) or an unreachable Spotify (502) is toasted. */
export function useCheckSpotifyTrackName() {
  const { api } = useApp();
  return useBandMutation<components["schemas"]["SpotifyTrackNameResult"], { link: string }>(
    async (band, { link }) => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/releases/spotify-track-name", {
        params: { path: { bandId: band } },
        body: { link },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { onError: toastError(false, "Couldn't check that on Spotify") },
  );
}

/** `POST /releases/{id}/tracks/{trackId}/spotify-label/refresh` (ART-219) — re-read a song's name from Spotify, the
 *  read-only label beside its Spotify link. 502 when Spotify could not be asked (toasted). → ReleaseTracksRead. */
export function useRefreshSpotifyLabel(releaseId: string) {
  const { api } = useApp();
  const invalidate = useReleaseInvalidator(releaseId);
  return useBandMutation<ReleaseTracksRead, { trackId: string }>(
    async (band, { trackId }) => {
      const { data, error, response } = await api.POST(
        "/api/v1/bands/{bandId}/releases/{releaseId}/tracks/{trackId}/spotify-label/refresh",
        { params: { path: { bandId: band, releaseId, trackId } } },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { onSuccess: invalidate, onError: toastError(false, "Couldn't reach Spotify") },
  );
}

/**
 * `POST /releases/{id}/dsp-links/sync` (ART-219) — the Smart Link section's "Sync DSP Links": the scheduled DSP
 * link lookup, run now for this release. Allowed to fail (the result's status says why — the caller words it), and
 * once a minute per release whether or not it worked: a press inside the minute is a 429, toasted here with the
 * one sentence that tells the person what to do.
 */
export function useSyncDspLinks(releaseId: string, opts?: MutationOpts) {
  const { api } = useApp();
  const invalidate = useReleaseInvalidator(releaseId);
  return useBandMutation<DspLinkSyncResult, void>(
    async (band) => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/releases/{releaseId}/dsp-links/sync", {
        params: { path: { bandId: band, releaseId } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: invalidate,
      // `silent` for the automatic follow-up after a track sync: that caller decides what (if anything) to say.
      onError: opts?.silent
        ? undefined
        : (err) =>
            pushToast(
              err instanceof ApiError && err.status === 429
                ? "DSP link fetch attempted too recently. Wait a minute and try again."
                : errorMessage(err, "Couldn't fetch DSP links"),
            ),
    },
  );
}

// ---- Track variants (downloadable parts — per-row add/remove, no If-Match) -----------------------

/** `POST /releases/{id}/tracks/{trackId}/variants` — attach a labeled downloadable part → ReleaseTracksRead. */
export function useAddTrackVariant(releaseId: string, opts?: MutationOpts) {
  const { api } = useApp();
  const invalidate = useReleaseInvalidator(releaseId);
  return useBandMutation<ReleaseTracksRead, { trackId: string; body: AddTrackVariantRequest }>(
    async (band, { trackId, body }) => {
      const { data, error, response } = await api.POST(
        "/api/v1/bands/{bandId}/releases/{releaseId}/tracks/{trackId}/variants",
        { params: { path: { bandId: band, releaseId, trackId } }, body },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { onSuccess: invalidate, onError: toastError(opts?.silent, "Couldn't add the version") },
  );
}

/** `DELETE .../tracks/{trackId}/variants/{variantId}` — remove a downloadable part → ReleaseTracksRead. */
export function useRemoveTrackVariant(releaseId: string, opts?: MutationOpts) {
  const { api } = useApp();
  const invalidate = useReleaseInvalidator(releaseId);
  return useBandMutation<ReleaseTracksRead, { trackId: string; variantId: string }>(
    async (band, { trackId, variantId }) => {
      const { data, error, response } = await api.DELETE(
        "/api/v1/bands/{bandId}/releases/{releaseId}/tracks/{trackId}/variants/{variantId}",
        { params: { path: { bandId: band, releaseId, trackId, variantId } } },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { onSuccess: invalidate, onError: toastError(opts?.silent, "Couldn't remove the version") },
  );
}

// ---- DSP links (full-set replace, REQUIRES If-Match) ---------------------------------------------

/**
 * `PUT /releases/{id}/dsp-links` — replace the per-DSP URL set (the smart-link buttons + the Antenna
 * dsp_links snapshot source). Full replace: the editor sends whatever the table holds. Sends
 * `If-Match: "<version>"` (the replace rides the release e_ver → the wire). 409 on a stale version. Returns
 * DspLinksRead; invalidates the composed record so the phone-mockup preview + the public link buttons repaint.
 */
export function useReplaceDspLinks(releaseId: string, opts?: MutationOpts) {
  const { api } = useApp();
  const invalidate = useReleaseInvalidator(releaseId);
  return useBandMutation<DspLinksRead, { version: number; body: DspLinksReplaceRequest }>(
    async (band, { version, body }) => {
      const { data, error, response } = await api.PUT("/api/v1/bands/{bandId}/releases/{releaseId}/dsp-links", {
        params: { path: { bandId: band, releaseId } },
        headers: { "If-Match": `"${version}"` },
        body,
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { onSuccess: invalidate, onError: toastError(opts?.silent, "Couldn't save the DSP links") },
  );
}

/**
 * The error SLUG on a caught 409, so the caller can distinguish `slug-taken` from `version-conflict`.
 * ProblemDetails.type is a full URI (`https://…/errors/slug-taken`, `ProblemTypes.Uri`), so we return its last
 * path segment — comparing the raw URI against a bare slug is always false (the review's dead-branch finding).
 */
export function problemType(err: unknown): string | undefined {
  if (err instanceof ApiError && err.problem && typeof err.problem === "object") {
    const type = (err.problem as { type?: string }).type;
    if (!type) return undefined;
    return type.split("/").filter(Boolean).pop();
  }
  return undefined;
}
